import { useEffect, useState } from 'react';
import axios from 'axios';
import Reveal from '../components/site/Reveal';
import { ExternalLink, ArrowUpRight } from 'lucide-react';
import { useT } from '../i18n/LanguageContext';
import { useExternalLink } from '../contexts/ExternalLinkContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Ventures() {
  const { t } = useT();
  const { confirm } = useExternalLink();
  const TABS = [
    { key: 'm3', label: t('ventures.m3Tab') || 'M³ Ventures' },
    { key: 'subsidiary', label: t('ventures.subsidiariesTab') || 'Subsidiaries' },
    { key: 'portfolio', label: t('ventures.portfolioTab') || 'Portfolio' },
  ];
  const [tab, setTab] = useState('m3');
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios.get(`${API}/ventures`).then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  const current = items.filter((v) => v.category === tab);

  return (
    <div data-testid="page-ventures">
      <section className="pt-40 pb-16 relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          <Reveal>
            <p className="eyebrow mb-6">{t('ventures.eyebrow')}</p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tighter text-prim max-w-4xl">
              A concentrated holding, <span className="italic text-brand-teal-light">held with patience.</span>
            </h1>
            <p className="mt-8 text-sec text-lg md:text-xl max-w-3xl leading-relaxed">
              {t('ventures.intro')}
            </p>
          </Reveal>

          <Reveal delay={200}>
            <div className="mt-12 inline-flex p-1 rounded-full border border-subtle surface-2">
              {TABS.map((T) => (
                <button
                  key={T.key}
                  data-testid={`ventures-tab-${T.key}`}
                  onClick={() => setTab(T.key)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                    tab === T.key ? 'bg-brand-maroon text-white' : 'text-sec hover:text-prim'
                  }`}
                >
                  {T.label}
                </button>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-16 border-t border-subtle">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
          {/* m3 layout with rich cards */}
          {tab === 'm3' && (
            <div data-testid="ventures-m3-section" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {current.map((v, i) => (
                <Reveal key={v.id} delay={(i % 3) * 100}>
                  <a
                    href={v.url || '#'}
                    onClick={(e) => { e.preventDefault(); if (v.url) confirm(v.url, v.name); }}
                    data-testid={`venture-card-${v.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="block group surface-2 border border-subtle p-8 rounded-md card-glow h-full"
                  >
                    <div className="flex items-start justify-between">
                      {v.arabic ? <p className="font-arabic text-3xl text-brand-teal-light/80">{v.arabic}</p> : <span />}
                      {v.status && (
                        <span className={`px-3 py-1 rounded-full text-[10px] font-mono tracking-[0.2em] uppercase ${
                          v.status === 'Operations'
                            ? 'bg-brand-teal/10 text-brand-teal-light border border-brand-teal/30'
                            : 'bg-brand-maroon/10 text-brand-maroon-light border border-brand-maroon/30'
                        }`}>{v.status}</span>
                      )}
                    </div>
                    {v.logo_base64 && (
                      <div className="mt-4 h-12 w-12 rounded-md overflow-hidden surface">
                        <img src={v.logo_base64} alt={v.name} className="photo-fit" />
                      </div>
                    )}
                    <h3 className="mt-6 font-display text-2xl text-prim tracking-tight flex items-center gap-2">
                      {v.name}
                      {v.url && <ArrowUpRight size={16} className="opacity-0 group-hover:opacity-100 transition-opacity text-brand-teal-light" />}
                    </h3>
                    {v.sector && <p className="mt-1 font-mono text-[11px] text-brand-teal-light/80">{v.sector}</p>}
                    {v.blurb && <p className="mt-4 text-sm text-sec leading-relaxed">{v.blurb}</p>}
                  </a>
                </Reveal>
              ))}
            </div>
          )}

          {/* Subsidiaries — compact list */}
          {tab === 'subsidiary' && (
            <div data-testid="ventures-subs-section" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {current.map((v, i) => (
                <Reveal key={v.id} delay={(i % 3) * 80}>
                  <a
                    href={v.url || '#'}
                    onClick={(e) => { e.preventDefault(); if (v.url) confirm(v.url, v.name); }}
                    data-testid={`subsidiary-${v.name.toLowerCase().replace(/[^a-z]/g, '')}`}
                    className="group flex items-center justify-between surface-2 border border-subtle p-6 rounded-md card-glow"
                  >
                    <div className="flex items-center gap-3">
                      {v.logo_base64 && (
                        <div className="w-10 h-10 rounded overflow-hidden surface shrink-0">
                          <img src={v.logo_base64} alt={v.name} className="photo-fit" />
                        </div>
                      )}
                      <div>
                        <h4 className="font-display text-xl text-prim tracking-tight">{v.name}</h4>
                        {v.sector && <p className="mt-1 font-mono text-[10px] tracking-[0.2em] uppercase text-brand-teal-light/80">{v.sector}</p>}
                      </div>
                    </div>
                    {v.url && <ExternalLink size={16} className="text-muted group-hover:text-brand-teal-light transition-colors" />}
                  </a>
                </Reveal>
              ))}
            </div>
          )}

          {/* Portfolio — wordmark tiles */}
          {tab === 'portfolio' && (
            <div data-testid="ventures-portfolio-section" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {current.map((v, i) => (
                <Reveal key={v.id} delay={(i % 4) * 60}>
                  <a
                    href={v.url || '#'}
                    onClick={(e) => { e.preventDefault(); if (v.url) confirm(v.url, v.name); }}
                    data-testid={`portfolio-${v.name.toLowerCase().replace(/[^a-z]/g, '')}`}
                    className="block surface-2 border border-subtle p-6 rounded-md card-glow text-center group"
                  >
                    <div className="aspect-[3/2] flex items-center justify-center bg-gradient-to-br from-brand-maroon/10 to-brand-teal/5 rounded overflow-hidden">
                      {v.logo_base64 ? (
                        <img src={v.logo_base64} alt={v.name} className="photo-fit" />
                      ) : (
                        <span className="font-display text-2xl text-prim group-hover:text-brand-teal-light transition-colors tracking-tight px-2">{v.name}</span>
                      )}
                    </div>
                  </a>
                </Reveal>
              ))}
            </div>
          )}

          {current.length === 0 && (
            <p className="text-sec text-sm font-mono">No items in this category yet.</p>
          )}
        </div>
      </section>
    </div>
  );
}
